import "./style.scss";
import { FC, useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "../../../../BtnDefault/BtnDefault";
import { Editor } from "@tinymce/tinymce-react";
import { splitHtmlByBlocks, splitHtmlByBlocksWithFontConvert } from "../../../converterPtToRem";
import { TINYMCE_EDITOR_CONTENT_STYLE, TINYMCE_EDITOR_HEIGHT } from "../../../tinyMceEditorDisplayStyle";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import AOPTaskIdInput from "@/src/components/AdmissionsOfficePage/shared/AOPTaskIdInput/AOPTaskIdInput";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { useAdmissionTaskPrefill } from "@/src/hooks/useAdmissionTaskPrefill";
import { requestLetterTaskPrefillMapping } from "@/src/constants/AdmissionOffice/admissionTaskPrefillMappings";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import { AOPUkraineRequestData } from "@/src/types/admissionOffice";
import { admissionOfficeWhoseDoc } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { reloadPageAfterNewDocumentConfirm } from "@/src/utils/admissionOffice/confirmNewDocumentReload";
import { useDispatch } from "react-redux";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { useAdmissionGeneratedDocumentActions } from "@/src/hooks/useAdmissionGeneratedDocumentActions";
import AOPGeneratedDocumentToolbar from "@/src/components/AdmissionsOfficePage/AOPGeneratedDocumentToolbar";
import CustomSwitch from "@/src/components/CustomUtils/CustomSwitch/CustomSwitch";
import { REQUEST_LETTER_BASIC_REGISTRY, type RequestLetterBasicCountry } from "../requestLetterBasicRegistry";

interface IAOPFormValues {
  whose_doc: string;
  coursePrefix: string;
  currentDate: string;
  documentNumber: string;
  PV: boolean;
  university: boolean;
  fullName: string;
  fullNameEn: string;
  dateOfBirth: string;
  passportNumber: string;
  bodyPV: string;
  secondBody: string;
  withPrint: boolean;
  task_id?: string;
}

interface AOPConfigProps {
  country: RequestLetterBasicCountry;
  dataForGeneration: AOPUkraineRequestData;
  setDataForGeneration: React.Dispatch<React.SetStateAction<AOPUkraineRequestData>>;
  setBodyForPrev: React.Dispatch<React.SetStateAction<AOPUkraineRequestData>>;
}

const today = new Date();
const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AOPConfig: FC<AOPConfigProps> = ({ country, dataForGeneration, setDataForGeneration, setBodyForPrev }) => {
  const { pvTemplates, universityTemplates } = REQUEST_LETTER_BASIC_REGISTRY[country];
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      bodyPV: "",
      secondBody: "",
      PV: true,
      university: false,
      withPrint: false,
      currentDate: formatDateForInput(today),
    },
  });

  const dispatch = useDispatch();
  const [content, setContent] = useState<string>("");
  const [secondContent, setSecondContent] = useState<string>("");

  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const generatedDoc = useAdmissionGeneratedDocumentActions({ getValues });

  const bodyPV = watch("bodyPV");
  const unisersityBody = watch("secondBody");
  const PV = watch("PV");
  const university = watch("university");
  const bodyPVAuto = useAutocomplete(Object.keys(pvTemplates));
  const unisersityBodyAuto = useAutocomplete(Object.keys(universityTemplates));
  const whoseDocAuto = useAutocomplete(admissionOfficeWhoseDoc);

  const { applyTaskPrefill, lastPrefilledTaskIdRef } = useAdmissionTaskPrefill<IAOPFormValues>({
    setValue,
    country,
    documentSlug: "request",
    mapping: requestLetterTaskPrefillMapping,
    onPrefill: (payload, mapping, formBaseline) => {
      generatedDoc.setTaskPrefillSnapshot(payload, mapping, formBaseline);
    },
  });

  useEffect(() => {
    if (!bodyPV) {
      setContent("");
      return;
    }
    const key = bodyPV as keyof typeof pvTemplates;
    const courseSettings = pvTemplates[key];
    setContent(courseSettings?.tinyMCEContent ?? "");
  }, [bodyPV, pvTemplates]);

  useEffect(() => {
    if (!unisersityBody) {
      setSecondContent("");
      return;
    }
    const key = unisersityBody as keyof typeof universityTemplates;
    const invoiceSettings = universityTemplates[key];
    setSecondContent(invoiceSettings?.tinyMCEContent ?? "");
  }, [unisersityBody, universityTemplates]);

  const applyTemplateVariables = (
    html: string,
    data: {
      fullName?: string;
      fullNameEn?: string;
      dateOfBirth?: string;
      passportNumber?: string;
    }
  ) => {
    return html
      .replaceAll("[fullName]", data.fullName || "")
      .replaceAll("[fullNameEn]", data.fullNameEn || "")
      .replaceAll("[dateOfBirth]", data.dateOfBirth || "")
      .replaceAll("[passportNumber]", data.passportNumber || "");
  };

  const generateNewDocument = async () => {
    generatedDoc.resetGeneratedState();
    dispatch(setIsPreloyder(true));
    try {
      const response = await admissionOfficeRequests.createUkraineRequestDoc(dataForGeneration);
      generatedDoc.onDocumentGenerated(response);
    } catch (error) {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
    setIsPreviewReady(false);
  };

  const handlePreview = () => {
    const watchedFields = {
      whose_doc: watch("whose_doc"),
      coursePrefix: watch("coursePrefix"),
      currentDate: watch("currentDate"),
      documentNumber: watch("documentNumber"),
      fullName: watch("fullName"),
      fullNameEn: watch("fullNameEn"),
      dateOfBirth: watch("dateOfBirth"),
      passportNumber: watch("passportNumber"),
      withPrint: watch("withPrint"),
    };

    const replacedPVContent = applyTemplateVariables(content, {
      fullName: watchedFields.fullName,
      fullNameEn: watchedFields.fullNameEn,
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      passportNumber: watchedFields.passportNumber,
    });

    const replacedUniversityContent = applyTemplateVariables(secondContent, {
      fullName: watchedFields.fullName,
      fullNameEn: watchedFields.fullNameEn,
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      passportNumber: watchedFields.passportNumber,
    });

    setDataForGeneration((prev) => ({
      ...prev,
      whose_doc: watchedFields.whose_doc || "",
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      fullName: watchedFields.fullName || "",
      fullNameEn: watchedFields.fullNameEn || "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      passportNumber: watchedFields.passportNumber || "",
      withPrint: watchedFields.withPrint,
      bodyPV: PV ? splitHtmlByBlocks(replacedPVContent) : undefined,
      documentNumber: watchedFields.documentNumber || "",
      secondBody: university ? splitHtmlByBlocks(replacedUniversityContent) : undefined,
    }));
    setBodyForPrev((prev) => ({
      ...prev,
      whose_doc: watchedFields.whose_doc || "",
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      fullName: watchedFields.fullName || "",
      fullNameEn: watchedFields.fullNameEn || "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      passportNumber: watchedFields.passportNumber || "",
      withPrint: watchedFields.withPrint,
      bodyPV: PV ? splitHtmlByBlocksWithFontConvert(replacedPVContent) : undefined,
      documentNumber: watchedFields.documentNumber || "",
      secondBody: university ? splitHtmlByBlocksWithFontConvert(replacedUniversityContent) : undefined,
    }));

    const hasContent = content.trim().length > 0 || secondContent.trim().length > 0;
    if (hasContent) {
      setIsPreviewReady(true);
      generatedDoc.resetGeneratedState();
    }
  };

  const handlePVEditorChange = (newPVContent: string) => setContent(newPVContent);
  const handleUniversityEditorChange = (newUniversityContent: string) => setSecondContent(newUniversityContent);

  function parseDate(value: string) {
    const [year, month, day] = value.split("-");
    return `${day}.${month}.${year}`;
  }

  const handleCopy = async (fieldName: string) => {
    const value = getValues(fieldName as keyof IAOPFormValues);
    if (!value) return;

    try {
      await navigator.clipboard.writeText(String(value));
    } catch (err) {
      console.error("Ошибка копирования", err);
    }
  };

  return (
    <>
      <div className="admission-office-page-config">
        <div className="admission-office-page-config-logo">
          <div className="logo-svg">
            <LogoSvg />
          </div>
          <div className="logo-line"></div>
          <p>Platforma</p>
        </div>
        <p className="admission-office-page-config-title">REQUEST LETTER</p>
        <form className="admission-office-page-config-form">
          <div className="form-group-fields">
            <div className="form-group-fields-container">
              <div className="form-group-fields-input whose_doc">
                <AutocompleteInput
                  placeholder="Whose"
                  value={whoseDocAuto.query}
                  onChange={whoseDocAuto.onChange}
                  onSelect={(v) => {
                    whoseDocAuto.onSelect(v);
                    setValue("whose_doc", v);
                  }}
                  isOpen={whoseDocAuto.isOpen}
                  options={whoseDocAuto.filtered}
                  error={!!errors.whose_doc}
                    inputProps={{
                    register: register("whose_doc"),
                    close: whoseDocAuto.close,
                    setOpen: whoseDocAuto.onClick,
                    onArrowClick: whoseDocAuto.onArrowClick,
                  }}
                  identifier={"whose"}
                />
              </div>
              <div className="form-group-fields-input coursePrefix">
                <input className="coursePrefix" id="coursePrefix" type="text" {...register("coursePrefix")} />
              </div>
            </div>
            <div className="form-group-fields-input currentDate">
              <div className="form-group-fields-input-label">
                <label htmlFor="currentDate">
                  Date of Issue
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="currentDate" id="currentDate" type="date" {...register("currentDate")} />
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="document-number">Document Number</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="document-number" id="document-number" type="text" {...register("documentNumber")} />
              </div>
            <div className="form-group-fields-pv">
              <Controller
                name="PV"
                control={control}
                render={({ field }) => <CustomSwitch label="PV" value={field.value} onChange={field.onChange} />}
              />
            </div>
            <div className="form-group-fields-university">
              <Controller
                name="university"
                control={control}
                render={({ field }) => <CustomSwitch label="University" value={field.value} onChange={field.onChange} />}
              />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name">
                  Full Name<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame" id="full-mame" type="text" {...register("fullName", { required: true })} />
              {errors.fullName && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullName")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name-en">
                  Full Name EN
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame-en" id="full-mame-en" type="text" {...register("fullNameEn")} />
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullNameEn")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="dateOfBirth">
                  Data of Birth
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="dateOfBirth" id="dateOfBirth" type="date" {...register("dateOfBirth")} />
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="passport-number">Passport Number</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="passport-number" id="passport-number" type="text" {...register("passportNumber")} />
              </div>
          </div>
          {PV && (
            <>
              <div className="form-group-fields">
                <div className="form-group-fields-input university-body">
                  <AutocompleteInput
                    label="Select body document PV"
                    value={bodyPVAuto.query}
                    onChange={bodyPVAuto.onChange}
                    onSelect={(v) => {
                      bodyPVAuto.onSelect(v);
                      setValue("bodyPV", v);
                    }}
                    isOpen={bodyPVAuto.isOpen}
                    options={bodyPVAuto.filtered}
                    error={!!errors.bodyPV}
                      inputProps={{
                      register: register("bodyPV"),
                      close: bodyPVAuto.close,
                      setOpen: bodyPVAuto.onClick,
                      onArrowClick: bodyPVAuto.onArrowClick,
                    }}
                    identifier={"pv"}
                  />
                </div>
              </div>
              <label>Request letter body (PV)</label>
              <Editor
                value={content}
                init={{
                  height: TINYMCE_EDITOR_HEIGHT,
                  menubar: false,
                  plugins: ["lists", "link", "code", "table"],
                  toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
                  branding: false,
                  toolbar_mode: "wrap",
                  licenseKey: "gpl",
                  license_key: "gpl" as any,
                  content_style: TINYMCE_EDITOR_CONTENT_STYLE,
                }}
                onEditorChange={handlePVEditorChange}
                tinymceScriptSrc="/tinymce/tinymce.min.js"
              />
            </>
          )}
          {university && (
            <>
              <div className="form-group-fields">
                <div className="form-group-fields-input university-body">
                  <AutocompleteInput
                    label="Select body document university"
                    value={unisersityBodyAuto.query}
                    onChange={unisersityBodyAuto.onChange}
                    onSelect={(v) => {
                      unisersityBodyAuto.onSelect(v);
                      setValue("secondBody", v);
                    }}
                    isOpen={unisersityBodyAuto.isOpen}
                    options={unisersityBodyAuto.filtered}
                    error={!!errors.secondBody}
                      inputProps={{
                      register: register("secondBody"),
                      close: unisersityBodyAuto.close,
                      setOpen: unisersityBodyAuto.onClick,
                      onArrowClick: unisersityBodyAuto.onArrowClick,
                    }}
                    identifier={"university"}
                  />
                </div>
              </div>
              <label>Request letter body (university)</label>
              <Editor
                value={secondContent}
                init={{
                  height: TINYMCE_EDITOR_HEIGHT,
                  menubar: false,
                  plugins: ["lists", "link", "code", "table"],
                  toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
                  branding: false,
                  toolbar_mode: "wrap",
                  licenseKey: "gpl",
                  license_key: "gpl" as any,
                  content_style: TINYMCE_EDITOR_CONTENT_STYLE,
                }}
                onEditorChange={handleUniversityEditorChange}
                tinymceScriptSrc="/tinymce/tinymce.min.js"
              />
            </>
          )}
          <AOPGeneratedDocumentToolbar
            isGenerated={generatedDoc.isGenerated}
            generatedPdfUrl={generatedDoc.generatedPdfUrl}
            taskAttachedPdfUrl={generatedDoc.taskAttachedPdfUrl}
            previewPdfUrl={generatedDoc.previewPdfUrl}
            canOpenPdfPreview={generatedDoc.canOpenPdfPreview}
            canOpenTaskDocumentEye={generatedDoc.canOpenTaskDocumentEye}
            isGeneratedPdfPreviewOpen={generatedDoc.isGeneratedPdfPreviewOpen}
            setIsGeneratedPdfPreviewOpen={generatedDoc.setIsGeneratedPdfPreviewOpen}
            generatedPreviewTitle={generatedDoc.generatedPreviewTitle}
            onDownload={generatedDoc.handleDownloadGeneratedPdf}
            onApprove={generatedDoc.handlePreviewApprove}
            onReject={generatedDoc.handlePreviewReject}
            isTaskDocumentCheckReview={generatedDoc.isTaskDocumentCheckReview}
            previewButton={
              <BtnDefault onClick={handlePreview} reverse={true}>
                <p>Preview doc</p>
                <AODSvgExport.PreviewSvg />
              </BtnDefault>
            }
            generateButton={
              <BtnDefault onClick={handleSubmit(generateNewDocument)} disabled={!isPreviewReady}>
                <p>Generate doc</p>
                <AODSvgExport.GenerateSvg />
              </BtnDefault>
            }
            trailingSlot={
              <>
                <div className="form-group-fields-print">
                  <Controller
                    name="withPrint"
                    control={control}
                    render={({ field }) => <CustomSwitch label="With Print" value={field.value} onChange={field.onChange} />}
                  />
                </div>
                <div className="form-group-buttons-refresh" onClick={reloadPageAfterNewDocumentConfirm}>
                  <BtnDefault onlyIcon={true}>
                    <AODSvgExport.RefreshSvg />
                  </BtnDefault>
                </div>
              </>
            }
          />
          <div className="form-group-fields">
            <AOPTaskIdInput
              register={register}
              setError={setError}
              clearErrors={clearErrors}
              applyTaskPrefill={applyTaskPrefill}
              lastPrefilledTaskIdRef={lastPrefilledTaskIdRef}
              onCopy={() => handleCopy("task_id")}
            />
          </div>
        </form>
      </div>
    </>
  );
};

export default AOPConfig;
